import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle, Ticket, Storefront } from "@phosphor-icons/react";

export default function Validate() {
  const { slotId } = useParams();
  const [sp] = useSearchParams();
  const urlToken = sp.get("t");
  const [data, setData] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get(`/analytics/slot/${slotId}`).then(({ data }) => setData(data)).catch(() => setData(false));
  }, [slotId]);

  const confirm = async () => {
    setLoading(true);
    try {
      let token = urlToken;
      if (!token) {
        const { data: t } = await api.get(`/slots/${slotId}/redeem-token`);
        token = t.token;
      }
      const { data: r } = await api.post(`/slots/${slotId}/redeem`, { token, source: "validate" });
      setConfirmed(true);
      setData((d) => ({ ...d, total_redemptions: r.total_redemptions }));
      toast.success("Redemption validated!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not validate");
    } finally {
      setLoading(false);
    }
  };

  if (data === null) return <div className="min-h-screen flex items-center justify-center text-slate-400">Loading…</div>;
  if (data === false || !data.slot) return <div className="min-h-screen flex items-center justify-center text-slate-400">Coupon not found.</div>;

  const slot = data.slot;
  const cap = slot.redemption_cap || 0;
  const reached = cap > 0 && data.total_redemptions >= cap;
  return (
    <div className="min-h-screen bg-navy-900 flex items-center justify-center p-6 relative">
      <div className="absolute inset-0 grain opacity-30" />
      <div className="relative w-full max-w-md bg-white border border-slate-200 p-10 shadow-2xl" data-testid="validate-card">
        <p className="text-xs uppercase tracking-[0.2em] font-bold text-amber-600">Merchant Validation</p>
        <div className="flex items-center gap-3 mt-3">
          <span className="w-12 h-12 rounded-sm bg-navy-900 text-white flex items-center justify-center shrink-0"><Storefront size={22} weight="duotone" /></span>
          <h1 className="font-display text-3xl tracking-tight text-navy-900 leading-tight">{slot.business_name}</h1>
        </div>
        <p className="text-emerald-800 font-[600] mt-4">{slot.offer_details}</p>
        <div className="mt-6 grid grid-cols-2 gap-px bg-slate-200 border border-slate-200">
          <div className="bg-white p-4"><p className="text-xs uppercase tracking-wider font-bold text-slate-500">Slot</p><p className="font-display text-2xl text-navy-900">{slot.face} · {slot.slot_number}</p></div>
          <div className="bg-white p-4"><p className="text-xs uppercase tracking-wider font-bold text-slate-500">Redemptions</p><p className="font-display text-2xl text-emerald-700" data-testid="validate-redemptions">{data.total_redemptions}{cap ? ` / ${cap}` : ""}</p></div>
        </div>
        {confirmed ? (
          <div className="mt-8 flex flex-col items-center text-center">
            <CheckCircle size={48} weight="fill" className="text-emerald-600" />
            <p className="font-display text-2xl tracking-tight mt-3">Redemption Confirmed</p>
            <p className="text-sm text-slate-500 mt-1">Logged to analytics.</p>
          </div>
        ) : (
          <button onClick={confirm} disabled={loading || reached} data-testid="validate-confirm-btn"
            className={`w-full mt-8 py-4 font-[600] transition-colors duration-200 flex items-center justify-center gap-2 ${reached ? "bg-slate-200 text-slate-400 cursor-not-allowed" : "bg-navy-900 text-white hover:bg-emerald-700 disabled:opacity-60"}`}>
            <Ticket size={18} /> {reached ? "Limit Reached — Offer Closed" : (loading ? "Validating…" : "Confirm In-Store Redemption")}
          </button>
        )}
        <Link to="/" className="block text-center text-sm text-slate-400 hover:text-navy-900 mt-6 transition-colors">Back to PMI</Link>
      </div>
    </div>
  );
}
